<script>
import Spinner from "@/components/ui/Spinner";
import { mapGetters } from "vuex";
import FormBox from "@/components/patient/dashboard/FormBox";
import IndicationForm from "@/components/appointment/indications/IndicationForm";
import NotesBox from "@/components/patient/dashboard/NotesBox";
import {
  isIndicationsEnabled,
  isObservationsEnabled,
  isAdmissionForDeragopyanEnabled,
  areImagesAndLabButtonsHidden,
} from "@/services/config";
import helperService from "@/services/helper";
import VideoRoom from "@/components/video-room/VideoRoom";
import { get } from "lodash";
import appointmentService from "@/services/appointment";
import indicationService from "@/services/indication";
import {
  filterPersistibleIndications,
  VIRTUAL_INDICATION_TYPES,
} from "@/services/indication-filter";
import indicationsDraftService from "@/services/indications-draft";
import debounce from "debounce";
import { format } from "date-fns";
import { mapBackendIndicationToFrontend } from "@/services/indication-mapper";
import formService from "@/services/form";
import programPatientService from "@/services/programPatient";
import { INDICATIONS_TYPES } from "@/services/constants";
import fileUploadConfigService from "@/services/fileupload-config";
import api from "@/services/api";
import { cloneDeep } from "lodash";
import SpinnerModal from "@/components/ui/SpinnerModal";
import AppointmentSummary from "@/components/appointment/AppointmentSummary";
import PatientHistory from "./PatientHistory.vue";
import patientService from "@/services/patient";
import labSsoService from "@/services/lab-sso";
import visualMedicaService from "@/services/visual-medica";
import {
  getMedicalInsuranceNumber,
  getMedicalInsurancePlan,
  getMedicalInsuranceName,
} from "@/services/patient-utils";
import ModalImageReports from "@/components/appointment/ModalImageReports";
import { hasPatientReports } from "@/services/patient-report";

const DRAFT_DEBOUNCE_MS = 1000;

export default {
  name: "PatientAppointmentView",
  components: {
    Spinner,
    SpinnerModal,
    FormBox,
    IndicationForm,
    NotesBox,
    VideoRoom,
    AppointmentSummary,
    PatientHistory,
    ModalImageReports,
  },

  data() {
    return {
      patient: undefined,
      appointment: "",
      videoRoomToken: "",
      isLoadingPatientData: true,
      isLoading: false,
      isSavingIndications: false,
      isAdmissionForDeragopyanEnabled,
      existingEvolution: null,
      roomInfo: {},
      isIndicationsEnabled,
      isObservationsEnabled,
      appointmentStatus: "appointment",
      // Se agrega filter para no mostrar imagesResults y labResults entre las opciones de creación
      forms: cloneDeep(
        INDICATIONS_TYPES.filter(
          (form) => form.type !== "imagesResults" && form.type !== "labResults"
        )
      ),
      savedIndications: [],
      savedFiles: {},
      completedForms: [],
      hasImageResults: false,
      imageReportsModalVisible: false,
      patientIndicationsFilter: [
        "medications",
        "images",
        "lab",
        "practices",
        "notes",
        "certificates",
      ],
      otherIndicationsFilter: ["evolution"],
      pacsConfig: null,
      isOnline: navigator.onLine,
      // El borrador no se escribe hasta resolver si hay uno para recuperar
      // (si no, las precargas del formulario lo pisarían) ni después de
      // finalizar la atención.
      isDraftReady: false,
      isDraftDisabled: false,
    };
  },

  watch: {
    savedIndications: {
      handler() {
        this.schedulePersistDraft();
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters(["isDoctor", "isAdmin", "isAdmissionist"]),

    get() {
      return get;
    },

    isPacsEnabled() {
      return this.pacsConfig?.imagePacs?.url;
    },

    isImagesButtonVisible() {
      return (
        !areImagesAndLabButtonsHidden &&
        this.isPacsEnabled &&
        !this.isVisualMedicaEnabled &&
        (this.isDoctor || this.isAdmin || this.isAdmissionist)
      );
    },

    isVisualMedicaEnabled() {
      return this.pacsConfig?.visualMedica?.enabled;
    },

    isVisualMedicaButtonVisible() {
      return (
        this.isVisualMedicaEnabled &&
        (this.isDoctor || this.isAdmin || this.isAdmissionist)
      );
    },

    isLabButtonVisible() {
      return (
        !areImagesAndLabButtonsHidden && (this.isDoctor || this.isAdmissionist)
      );
    },

    isImageReportsButtonVisible() {
      return (
        this.hasImageResults &&
        (this.isDoctor || this.isAdmin || this.isAdmissionist)
      );
    },

    canSaveIndications() {
      const hasIndications =
        this.savedIndications.length > 0 &&
        this.savedIndications.some(
          (indication) =>
            !indication.createdAt &&
            this.patientIndicationsFilter.includes(indication.type)
        );

      const hasCompletedForms = this.completedForms.length > 0;

      const hasCompletedPrograms = this.hasCompletedPrograms();

      return (
        !this.isSavingIndications &&
        (hasIndications || hasCompletedForms || hasCompletedPrograms)
      );
    },

    getAllCompletedForms() {
      return this.completedForms;
    },

    truncatedObservation() {
      const obs = this.appointment?.observation || "";
      return obs.length > 100 ? obs.substring(0, 100) + "..." : obs;
    },
  },

  created() {
    this.fetchAppointmentData();
    this.loadPacsConfig();

    this.$bus.$on("updateIndication", (indication, isDeleted = false) => {
      const indicationIndex = this.savedIndications.findIndex(
        (e) => e.key === indication.key
      );
      if (indicationIndex !== -1) {
        if (!isDeleted) {
          this.$set(this.savedIndications, indicationIndex, indication);
        } else {
          this.savedIndications.splice(indicationIndex, 1);
        }
      } else {
        if (!isDeleted) {
          this.savedIndications.push(indication);
        }
      }
    });

    this.$bus.$on("filesUploaded", (indicationKey) => {
      const indicationIndex = this.savedIndications.findIndex(
        (e) => e.key === indicationKey
      );
      if (indicationIndex !== -1) {
        this.$set(this.savedIndications, indicationIndex, {
          ...this.savedIndications[indicationIndex],
          hasFiles: false,
        });
      }

      if (this.savedIndications.every((indication) => !indication.hasFiles)) {
        this.isSavingIndications = false;
      }
    });

    this.$bus.$on("form-completed", this.updateCompletedForms);
    this.$bus.$on("form-updated", this.updateCompletedForms);
    this.$bus.$on("program-form-completed", () => {
      this.$forceUpdate();
    });

    this.debouncedPersistDraft = debounce(this.persistDraft, DRAFT_DEBOUNCE_MS);
    this.$bus.$on("indication-draft-changed", this.schedulePersistDraft);

    window.addEventListener("online", this.onOnline);
    window.addEventListener("offline", this.onOffline);
    window.addEventListener("beforeunload", this.onBeforeUnload);
    window.addEventListener("pagehide", this.flushDraft);
  },

  beforeDestroy() {
    this.flushDraft();
    this.$bus.$off("indication-draft-changed", this.schedulePersistDraft);
    window.removeEventListener("online", this.onOnline);
    window.removeEventListener("offline", this.onOffline);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    window.removeEventListener("pagehide", this.flushDraft);
    this.$bus.$off("form-completed", this.updateCompletedForms);
    this.$bus.$off("form-updated", this.updateCompletedForms);
    this.$bus.$off("program-form-completed");
  },

  methods: {
    onOnline() {
      this.isOnline = true;
      this.$message.success(
        "Se restableció la conexión. Ya podés enviar o finalizar la atención."
      );
    },

    onOffline() {
      this.isOnline = false;
    },

    hasUnsavedChanges() {
      const hasPendingIndications = this.savedIndications.some(
        (indication) =>
          !indication.createdAt &&
          !VIRTUAL_INDICATION_TYPES.includes(indication.type)
      );
      const hasDraft = !!indicationsDraftService.getDraft(
        ...this.getDraftIds()
      );
      return hasPendingIndications || hasDraft;
    },

    onBeforeUnload(event) {
      this.flushDraft();
      if (!this.hasUnsavedChanges()) return;
      event.preventDefault();
      event.returnValue = "";
    },

    getDraftIds() {
      return [this.$store.state.user?._id, this.appointment?._id];
    },

    getIndicationComponents() {
      const allIndicationRefs = this.$refs.indicationForm?.$refs || {};
      return Object.keys(allIndicationRefs)
        .filter((refName) => refName.startsWith("indications-"))
        .map((refName) => allIndicationRefs[refName]?.[0])
        .filter(Boolean);
    },

    schedulePersistDraft() {
      if (!this.isDraftReady || this.isDraftDisabled) return;
      this.debouncedPersistDraft();
    },

    flushDraft() {
      this.debouncedPersistDraft?.flush();
    },

    // Arma el borrador del turno: lo cargado en cada formulario, lo que quedó
    // solo en el resumen (p. ej. una prescripción repetida) y los datos de
    // catálogo que se necesitan para enviarlo, porque esos caches no
    // sobreviven a una recarga.
    buildDraftContent() {
      const forms = {};
      const formKeys = new Set();
      for (const component of this.getIndicationComponents()) {
        component.indications.forEach((i) => formKeys.add(i.key));
        const items = component.getDraftState?.() || [];
        if (items.length) forms[component.form.type] = items;
      }

      const summary = this.savedIndications
        .filter(
          (indication) =>
            !indication.createdAt &&
            !VIRTUAL_INDICATION_TYPES.includes(indication.type) &&
            !formKeys.has(indication.key)
        )
        // Los archivos adjuntos no se pueden guardar en localStorage.
        // eslint-disable-next-line no-unused-vars
        .map(({ files, hasFiles, ...indication }) => indication);

      if (!Object.keys(forms).length && !summary.length) return null;

      const ids = new Set();
      const collectIds = (value) => {
        if (typeof value === "string" && value) ids.add(value);
        else if (Array.isArray(value)) value.forEach(collectIds);
      };
      Object.values(forms)
        .flat()
        .forEach((item) => Object.values(item.values).forEach(collectIds));
      summary.forEach((indication) =>
        Object.values(indication.defaultValues || {}).forEach((question) =>
          collectIds(question?.value)
        )
      );
      const pick = (cache) => [...ids].map((id) => cache?.[id]).filter(Boolean);
      const { state } = this.$store;

      return {
        forms,
        summary,
        catalog: {
          diagnoses: pick(state.cacheDiagnoses),
          medications: pick(state.cacheMedications),
          images: pick(state.cacheImages),
          labs: pick(state.cacheLabs),
        },
      };
    },

    persistDraft() {
      if (!this.isDraftReady || this.isDraftDisabled) return;
      const content = this.buildDraftContent();
      if (content) {
        indicationsDraftService.saveDraft(...this.getDraftIds(), content);
      } else {
        indicationsDraftService.removeDraft(...this.getDraftIds());
      }
    },

    getDraftTypeNames(draft) {
      const types = new Set([
        ...Object.keys(draft.forms || {}),
        ...(draft.summary || []).map((indication) => indication.type),
      ]);
      return [...types].map(
        (type) => this.forms.find((form) => form.type === type)?.name || type
      );
    },

    async offerDraftRestore() {
      if (this._isDestroyed) return;
      const draft = indicationsDraftService.getDraft(...this.getDraftIds());
      if (!draft) {
        this.isDraftReady = true;
        return;
      }

      const h = this.$createElement;
      try {
        await this.$confirm(
          h("div", null, [
            h(
              "p",
              null,
              `Tenés indicaciones sin enviar del ${format(
                new Date(draft.savedAt),
                "dd/MM/yyyy HH:mm"
              )}:`
            ),
            h(
              "ul",
              { style: "margin: 8px 0 8px 20px; list-style: disc;" },
              this.getDraftTypeNames(draft).map((name) =>
                h("li", { style: "font-weight: 600;" }, name)
              )
            ),
            h("p", null, "¿Querés recuperarlas?"),
          ]),
          "Borrador de la atención",
          {
            confirmButtonText: "Recuperar",
            cancelButtonText: "Descartar",
            type: "warning",
            showClose: false,
            closeOnClickModal: false,
            closeOnPressEscape: false,
          }
        );
        this.applyDraft(draft);
      } catch (error) {
        indicationsDraftService.removeDraft(...this.getDraftIds());
      } finally {
        this.isDraftReady = true;
      }
    },

    applyDraft(draft) {
      const { catalog = {} } = draft;
      (catalog.diagnoses || []).forEach((d) =>
        this.$store.dispatch("addDiagnoses", d)
      );
      (catalog.medications || []).forEach((m) =>
        this.$store.dispatch("addMedication", m)
      );
      (catalog.images || []).forEach((i) =>
        this.$store.dispatch("addImages", i)
      );
      (catalog.labs || []).forEach((l) => this.$store.dispatch("addLabs", l));

      for (const component of this.getIndicationComponents()) {
        component.restoreDraftState(draft.forms?.[component.form.type]);
      }

      (draft.summary || []).forEach((indication) => {
        if (this.savedIndications.some((saved) => saved.key === indication.key))
          return;
        this.savedIndications.push({
          ...indication,
          lockedAt: indication.lockedAt
            ? new Date(indication.lockedAt)
            : new Date(),
        });
      });
    },

    removeIndicationFromSummary(key) {
      const index = this.savedIndications.findIndex((i) => i.key === key);
      if (index !== -1) {
        this.savedIndications.splice(index, 1);
      }
      this.$bus.$emit("deleteIndicationLocal", key);
    },

    updateCompletedForms() {
      const indicationFormRef = this.$refs.indicationForm;
      if (!indicationFormRef) return;

      const allForms = [];
      const allIndicationRefs = indicationFormRef.$refs;

      for (const refName of Object.keys(allIndicationRefs)) {
        if (refName.startsWith("indications-")) {
          const indicationComponent = allIndicationRefs[refName];
          if (indicationComponent?.[0]?.completedForms) {
            const completedForms = indicationComponent[0].completedForms;
            for (const indicationKey of Object.keys(completedForms)) {
              allForms.push(...completedForms[indicationKey]);
            }
          }
        }
      }
      this.completedForms = allForms;
    },

    hasCompletedPrograms() {
      try {
        const indicationFormRef = this.$refs.indicationForm;
        if (!indicationFormRef) return false;

        const allIndicationRefs = indicationFormRef.$refs;

        for (const refName of Object.keys(allIndicationRefs)) {
          if (refName.startsWith("indications-")) {
            const indicationComponent = allIndicationRefs[refName];

            if (indicationComponent?.[0]?.completedProgramForms) {
              const completedProgramForms =
                indicationComponent[0].completedProgramForms;

              for (const indicationKey of Object.keys(completedProgramForms)) {
                const programForms = completedProgramForms[indicationKey];

                for (const programId of Object.keys(programForms)) {
                  const formsToSend = programForms[programId];

                  if (formsToSend && formsToSend.length > 0) {
                    return true;
                  }
                }
              }
            }
          }
        }

        return false;
      } catch (error) {
        console.error("Error al verificar programas completados:", error);
        return false;
      }
    },
    triggerUpload(createdIndication) {
      this.$refs.indicationForm.triggerUpload(createdIndication);
    },

    async loadExistingIndications(patientId, appointmentId) {
      try {
        const indications = await indicationService.getByAppointment(
          patientId,
          appointmentId
        );
        const mapped = indications
          .map(mapBackendIndicationToFrontend)
          .filter(Boolean);
        if (mapped.length > 0) {
          this.savedIndications = mapped;
        }
        const evolution = indications.find((i) => i.type === "evolution");
        if (evolution) {
          this.existingEvolution = evolution;
        }
      } catch (error) {
        console.error("Error al cargar indicaciones existentes:", error);
      }
    },

    async fetchAppointmentData() {
      try {
        const appointment = await appointmentService.getAppointmentById({
          id: this.$route.params.appointmentId,
          params: { populate: ["institution", "medicalSpecialty", "patient"] },
        });

        if (appointment) {
          this.appointment = appointment;
          this.patient = appointment.patient;
          this.checkImageResults(appointment.patient._id);
          // El borrador se ofrece cuando ya están las indicaciones del
          // servidor y los formularios montados, para comparar contra ellas.
          this.loadExistingIndications(appointment.patient._id, appointment._id)
            .then(() => this.$nextTick())
            .then(() => this.offerDraftRestore());

          if (this.isAdmissionForDeragopyanEnabled) {
            if (appointment.status === "pending") {
              this.$message.error(
                "El paciente debe ser admitido para iniciar la consulta."
              );
              this.$router.back();
              return;
            }

            if (
              appointment.status !== "admitted" &&
              appointment.status !== "started" &&
              appointment.status !== "done"
            ) {
              this.$message.error(
                "No se puede iniciar la atención: el estado de la cita no es válido."
              );
              this.$router.back();
              return;
            }

            if (appointment.status !== "done") {
              try {
                const updated = await appointmentService.startAppointment(
                  appointment._id
                );
                if (updated) {
                  this.appointment = updated;
                }
              } catch (startError) {
                this.$message.error(
                  "No se puede iniciar la atención. Verifique el estado de la cita."
                );
                this.$router.back();
                return;
              }
            }
          }
        }
      } catch (error) {
        this.$message.error(
          "Ocurrió un error al validar el estado de la cita. No se permite iniciar la sesión."
        );
        this.$router.back();
      } finally {
        this.isLoadingPatientData = false;
      }
    },

    goBack() {
      this.$router.back();
    },

    goToPatientDashboardTab() {
      let routeData = this.$router.resolve({
        name: "patient-dashboard",
        params: { id: this.patient._id },
      });
      window.open(routeData.href, "_blank");
    },

    getFullName(user) {
      return helperService.getFullName(user);
    },

    getGender(user) {
      switch (user.gender) {
        case "m":
          return "Hombre";

        case "f":
          return "Mujer";

        default:
          return "Otro";
      }
    },

    setAppointmentStatus(status) {
      this.appointmentStatus = status;
    },

    isHighlighted(value) {
      if (value === this.appointmentStatus) {
        return "selected";
      } else {
        return "not-selected";
      }
    },

    getMedicalInsuranceNumber,
    getMedicalInsurancePlan,
    getMedicalInsuranceName,

    async saveIndications(
      typeFilter = null,
      { silent = false, throwOnError = false } = {}
    ) {
      // La bandera se levanta acá, de forma sincrónica, antes de cualquier
      // await. Si se deja que la ponga el servicio, un segundo clic (o
      // "Finalizar atención" apenas después de guardar) entra mientras la
      // primera llamada sigue en vuelo y crea una orden duplicada.
      if (this.isSavingIndications) {
        return;
      }
      this.isSavingIndications = true;

      try {
        const validIndications = filterPersistibleIndications(
          this.savedIndications,
          typeFilter
        );
        await indicationService.saveIndications(
          validIndications,
          this.patient._id,
          this.appointment._id,
          typeFilter,
          this.$store,
          {
            onTriggerUpload: this.triggerUpload.bind(this),
            onUpdateIndication: (updatedIndication) => {
              const index = this.savedIndications.findIndex(
                (ind) => ind.key === updatedIndication.key
              );
              if (index !== -1) {
                this.$set(this.savedIndications, index, updatedIndication);
              }
            },
          }
        );

        await this.sendCompletedForms();
        if (!silent) {
          this.$message.success("Prescripciones enviadas con éxito");
        }
      } catch (error) {
        console.log(error);
        if (throwOnError) throw error;
      } finally {
        // Si quedaron archivos subiéndose, el guardado sigue abierto y lo
        // libera el evento "filesUploaded".
        if (this.savedIndications.every((indication) => !indication.hasFiles)) {
          this.isSavingIndications = false;
        }
      }
    },

    getUnsavedIndicationNames() {
      return this.getIndicationComponents()
        .filter((component) => component.getUnsavedIndications?.().length)
        .map((component) => component.form.name);
    },

    async confirmUnsavedIndications() {
      const names = this.getUnsavedIndicationNames();
      if (names.length === 0) return true;

      const h = this.$createElement;
      try {
        await this.$confirm(
          h("div", null, [
            h(
              "p",
              null,
              "Hay indicaciones con datos cargados que todavía no guardaste:"
            ),
            h(
              "ul",
              { style: "margin: 8px 0 8px 20px; list-style: disc;" },
              names.map((name) => h("li", { style: "font-weight: 600;" }, name))
            ),
            h(
              "p",
              null,
              "Si finalizás ahora no se van a enviar. ¿Querés finalizar igual o volver a terminarlas?"
            ),
          ]),
          "Indicaciones sin guardar",
          {
            confirmButtonText: "Finalizar igual",
            cancelButtonText: "Volver a completarlas",
            type: "warning",
          }
        );
        return true;
      } catch (error) {
        this.appointmentStatus = "appointment";
        return false;
      }
    },

    async endAppointment() {
      if (!(await this.confirmUnsavedIndications())) return;

      if (!navigator.onLine) {
        this.$message.warning(
          "No hay conexión a internet. Lo que cargaste en las indicaciones quedó guardado en este equipo; finalizá la atención cuando vuelva la conexión."
        );
        return;
      }

      try {
        await this.saveIndications(null, { silent: true, throwOnError: true });
        if (this.appointment.status !== "done") {
          await appointmentService.endAppointment(this.appointment._id);
        }
        this.$message.success("Atención finalizada con éxito");

        // Lo que no se guardó se descartó a conciencia al confirmar el aviso.
        this.isDraftDisabled = true;
        this.debouncedPersistDraft.clear();
        indicationsDraftService.removeDraft(...this.getDraftIds());

        this.$router.push({
          name: "appointment-schedule",
        });
      } catch (error) {
        console.log(error);
        // Sin respuesta del servidor: casi siempre es un corte de red.
        if (!error?.response) {
          this.$message.error(
            "No se pudo conectar con el servidor. No se perdió nada: las indicaciones quedaron guardadas en este equipo. Reintentá en unos segundos."
          );
          return;
        }
        this.$message.error(
          "Ocurrió un error al enviar las prescripciones. Intentá enviarlas manualmente antes de finalizar."
        );
      }
    },

    async sendCompletedForms() {
      try {
        const indicationFormRef = this.$refs.indicationForm;
        if (!indicationFormRef) return;

        const allIndicationRefs = indicationFormRef.$refs;

        for (const refName of Object.keys(allIndicationRefs)) {
          if (refName.startsWith("indications-")) {
            const indicationComponent = allIndicationRefs[refName];

            if (indicationComponent?.[0]?.completedForms) {
              const completedForms = indicationComponent[0].completedForms;

              for (const indicationKey of Object.keys(completedForms)) {
                const formsToSend = completedForms[indicationKey];

                for (const completedForm of formsToSend) {
                  if (!completedForm.isSent) {
                    await this.sendFormToBackend(
                      completedForm,
                      indicationKey,
                      indicationComponent[0]
                    );
                  }
                }
              }
            }

            if (indicationComponent?.[0]?.completedProgramForms) {
              const completedProgramForms =
                indicationComponent[0].completedProgramForms;

              for (const indicationKey of Object.keys(completedProgramForms)) {
                const programForms = completedProgramForms[indicationKey];

                for (const programId of Object.keys(programForms)) {
                  const formsToSend = programForms[programId];

                  if (formsToSend && formsToSend.length > 0) {
                    await this.sendProgramToBackend(programId, formsToSend);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error al enviar formularios completados:", error);
      }
    },

    async sendFormToBackend(completedForm, indicationKey, indicationComponent) {
      try {
        const formData = {
          form: completedForm.form?._id || completedForm._id,
          patient: this.patient._id,
          appointment: this.appointment._id,
          sections: completedForm.sections || [],
          answeredAt: completedForm.answeredAt || new Date(),
          status: completedForm.status || "open",
          createdBy: this.$store.state.user,
        };

        const createdForm = await formService.postPatientForm(formData);

        completedForm.isSent = true;
        completedForm._id = createdForm._id;
        completedForm.sentAt = new Date();

        if (completedForm.hasFiles && completedForm.dropzoneFiles?.length > 0) {
          await this.uploadFormFiles(
            createdForm._id,
            completedForm.dropzoneFiles
          );
        }

        this.updateIndication(indicationComponent);
      } catch (error) {
        console.error("Error al enviar formulario al backend:", error);
      }
    },

    async uploadFormFiles(formId, files) {
      try {
        const formData = new FormData();

        files.forEach((file) => {
          const nativeFile = file.file || file;
          if (nativeFile instanceof File || nativeFile instanceof Blob) {
            formData.append("files", nativeFile, nativeFile.name);
          }
        });

        const uploadUrl = fileUploadConfigService.getFormFilesEndpoint(formId);
        await api.put(uploadUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.$message.success("Archivos adjuntos subidos exitosamente");
      } catch (error) {
        console.error("Error al subir archivos del formulario:", error);
        this.$message.error(
          `Error al subir archivos: ${
            error.response?.data?.error || error.message
          }`
        );
      }
    },

    async sendProgramToBackend(programId, completedForms) {
      try {
        const completedFormsData = completedForms.map((completedForm) => ({
          name:
            completedForm.name ||
            completedForm.form?.name ||
            "Formulario del Programa",
          description:
            completedForm.description ||
            completedForm.form?.description ||
            "Formulario completado del programa",
          sections: completedForm.sections || [],
          status: "closed",
          answeredAt:
            completedForm.answeredAt ||
            completedForm.createdAt ||
            new Date().toISOString(),
        }));

        const programData = {
          program: programId,
          patient: this.patient._id,
          appointment: this.appointment._id,
          status: "pending",
          completedForms: completedFormsData,
        };

        const result = await programPatientService.assignProgramToPatient(
          programData
        );

        if (result.initialData && Array.isArray(result.initialData)) {
          for (let i = 0; i < completedForms.length; i++) {
            const completedForm = completedForms[i];
            const createdForm = result.initialData[i];

            if (
              completedForm.hasFiles &&
              completedForm.dropzoneFiles?.length > 0 &&
              createdForm?._id
            ) {
              await this.uploadFormFiles(
                createdForm._id,
                completedForm.dropzoneFiles
              );
            }
          }
        }

        completedForms.forEach((completedForm) => {
          completedForm.isSent = true;
          completedForm.sentAt = new Date();
        });
      } catch (error) {
        console.error("Error al enviar programa al backend:", error);
      }
    },

    updateIndication(indication) {
      this.$bus.$emit("updateIndication", cloneDeep(indication), false);
    },

    async loadPacsConfig() {
      try {
        const response = await patientService.getPacsConfig();
        this.pacsConfig = response.config;
      } catch (error) {
        console.error("Error al cargar configuración PACS:", error);
        this.pacsConfig = null;
      }
    },

    async checkImageResults(patientId) {
      this.hasImageResults = await hasPatientReports(patientId);
    },

    async openLab() {
      try {
        const { redirectUrl } = await labSsoService.getRedirectUrl(
          this.patient._id
        );
        window.open(redirectUrl, "_blank");
      } catch (error) {
        const message =
          error.response?.data?.error || "Error al acceder al laboratorio";
        this.$message.error(message);
      }
    },

    async openVisualMedica() {
      try {
        const { redirectUrl } = await visualMedicaService.getRedirectUrl(
          this.patient._id
        );
        window.open(redirectUrl, "_blank");
      } catch (error) {
        const status = error.response?.status;
        if (status === 404) {
          this.$message.info(
            "El paciente no tiene estudios de imágenes disponibles en el visor"
          );
          return;
        }
        const message =
          error.response?.data?.error || "Error al abrir el visor de imágenes";
        this.$message.error(message);
      }
    },

    openImages() {
      if (!this.isPacsEnabled) {
        this.$message.error("Sistema de imágenes no configurado");
        return;
      }

      const patientPublicId = this.patient?.publicId;
      if (!patientPublicId) {
        this.$message.error("El paciente no tiene ID público configurado");
        return;
      }

      const { url, user, pass } = this.pacsConfig.imagePacs;
      const fullUrl = `${url}?user_name=${encodeURIComponent(
        user
      )}&password=${encodeURIComponent(pass)}&patient_id=${encodeURIComponent(
        patientPublicId
      )}`;

      window.open(fullUrl, "_blank");
    },
  },
};
</script>

<template lang="pug">
  div
    spinner-modal(v-show="isSavingIndications" text="Guardando")
    .offline-banner(v-if="!isOnline")
      | Sin conexión a internet. Lo que cargues en las indicaciones se guarda en este equipo; enviá o finalizá la atención cuando vuelva la conexión.
    section
      .whiteBox(v-if="patient")
        .patient-header-container.top
          .patient-header-top
            p.secondary-text Atendiendo a
            h1(@click='goToPatientDashboardTab()') {{ getFullName(patient) }}
          .patient-header-top
            //- Botón "Ver adjuntos" oculto durante la atención en videoroom
            button.button-blue-new(v-if="isImagesButtonVisible" @click="openImages") Imágenes
            button.button-blue-new(v-if="isVisualMedicaButtonVisible" @click="openVisualMedica") Imágenes
            button.button-blue-new(v-if="isLabButtonVisible" @click="openLab") Laboratorio
            button.button-blue-new(v-if="isImageReportsButtonVisible" @click="imageReportsModalVisible = true") Informes
            button.button-blue-new(
              @click="saveIndications(patientIndicationsFilter)"
              :disabled="!canSaveIndications || isSavingIndications"
            ) Enviar prescripciones
            button.button-red-new(:disabled="isSavingIndications" @click="endAppointment") Finalizar atención
        .patient-header-container
          .patient-header-bottom
            p.secondary-text Edad
            p.primary-text {{ patient.bornAt | age }}
          .patient-header-bottom
            p.secondary-text DNI
            p.primary-text {{ get(patient, 'governmentId.number', 'N/A') }}
          .patient-header-bottom
            p.secondary-text Teléfono
            p.primary-text {{ patient.phone }}
          .patient-header-bottom
            p.secondary-text E-mail
            p.primary-text {{ patient.email }}
          .patient-header-bottom
            p.secondary-text Seguro Salud
            p.primary-text {{ getMedicalInsuranceName(patient) }}
          .patient-header-bottom
            p.secondary-text Cobertura
            p.primary-text {{ getMedicalInsurancePlan(patient) }}
          .patient-header-bottom
            p.secondary-text # de socio
            p.primary-text {{ getMedicalInsuranceNumber(patient) }}
          .patient-header-bottom(v-if="isObservationsEnabled && appointment.observation")
            p.secondary-text Observaciones
            p.primary-text {{ truncatedObservation }}
      
      .patient-attention-container(v-if="patient")
        .patient-attention-left
          .patient-attention-header
            p(:class="isHighlighted('appointment')" @click="setAppointmentStatus('appointment')") Atención
            p(:class="isHighlighted('history')" @click="setAppointmentStatus('history')") Historia clínica

          indication-form(v-show="isIndicationsEnabled && appointmentStatus === 'appointment'"
            ref="indicationForm"
            :forms="forms"
            :savedIndications="savedIndications"
            :appointment="appointment"
            :patient="patient"
            :existingEvolution="existingEvolution"
          )

          patient-history(v-show="appointmentStatus === 'history' && patient" :patientId="patient._id")

        .patient-attention-right
            video-room(v-if="this.appointment.attentionType === 'virtual'" :patientId="patient._id" :appointmentId="appointment.id")
            AppointmentSummary(
              :savedIndications="savedIndications"
              :context="'appointment'"
              :completedForms="getAllCompletedForms"
              @remove-indication="removeIndicationFromSummary"
            )

    modal-image-reports(
      :visible.sync="imageReportsModalVisible"
      :patient-id="patient ? patient._id : null"
      :patient-name="patient ? getFullName(patient) : ''"
    )
</template>

<style lang="scss" scoped>
.offline-banner {
  position: sticky;
  top: 0;
  z-index: 10;
  margin-bottom: 15px;
  padding: 10px 20px;
  border-radius: 10px;
  background-color: #fdf6ec;
  border: 1px solid #f5dab1;
  color: #b88230;
  font-weight: 600;
  text-align: center;
}

.headline {
  flex-direction: column;
}

.headline__title {
  display: flex;
  align-items: center;
}

.patient {
  text-decoration: underline;
  cursor: pointer;
  display: inline;
}

.back {
  margin-right: 10px;
}

h2 {
  margin-bottom: 25px;
  text-align: center;
}

h3 {
  margin-top: 15px;
  margin-left: 10px;
}

.video-room {
  display: grid;
  grid-template-columns: 50% 50%;
  grid-gap: 20px;
  height: 75vh;

  .sidebar {
    height: 100%;
    max-height: 70vh;
    width: 100%;
    margin-left: auto;

    .patientData {
      height: 100%;
      overflow: scroll;
      padding: 0 10px 0 0;
    }
  }
}

.patient-attention-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: row;

  .patient-attention-right {
    width: 500px;
    margin-left: 20px;
    background-color: $white;
    border-radius: 10px;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
    min-height: 500px;
    overflow: hidden;
  }

  .patient-attention-left {
    width: 100%;
    padding: 15px 25px;
    margin-bottom: 20px;
    background-color: $white;
    border-radius: 10px;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
    min-height: 600px;

    .patient-attention-header {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 30px;

      .selected {
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        color: $steel-blue;
        font-weight: 600;
        border-bottom: solid 2px $steel-blue;
        width: 100%;
        padding-bottom: 10px;
        margin-right: 2px;
      }

      .not-selected {
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        color: $primary;
        font-weight: 600;
        border-bottom: solid 2px rgba(0, 0, 0, 0.153);
        width: 100%;
        padding-bottom: 10px;
        margin-left: 2px;
      }
    }
  }
}

.whiteBox {
  width: 100%;
  padding: 15px 25px;
  margin-bottom: 20px;
  background-color: $white;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);

  .secondary-text {
    font-size: 13px;
    margin: 7px 0;
  }

  .primary-text {
    color: $primary;
    font-weight: 600;
    margin: 7px 0;
  }

  .patient-header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .patient-header-bottom {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    }
  }

  .patient-header-container.top {
    padding-bottom: 15px;
    margin-bottom: 10px;
    border-bottom: solid 1px #00000024;

    .patient-header-top {
      display: flex;
      justify-content: center;
      align-items: center;

      h1 {
        margin: 0 15px;
      }
    }
  }
}
</style>
